import Axios from 'axios';
import { action, observable, computed } from 'mobx';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { mySetInterval } from '@/utils/fn';

export interface PositionItem {
  symbolName: string; // 交易对名称
  autoMargin: boolean; // 是否自动最佳保证金
  availableCloseSize: string; // 可平仓数量（张）
  closeOrderSize: number; // 平仓挂单数量
  entryPrice: string; // 持仓成本价
  isolatedMargin: string; // 逐仓保证金
  leverage: number; // 杠杆倍数
  openOrderMarginFrozen: string; // 开仓订单冻结保证金
  positionSide: PositionSide; // 持仓方向
  positionSize: string; // 持仓数量
  positionType: PositionType; // 仓位类型
  positionModel: PositionMode; // 仓位模式
  realizedProfit: string; // 已实现盈亏
  state: number; // 仓位状态 0：无；1：使用中；2：强平中；3：冻结
  symbol: string; // 交易对
  unsettledProfit: string; // 开仓手续费
  positionId: string; // 仓位ID
  isLeaderPosition?: boolean; // 是否为带单仓位（交易员下单）
  balanceType?: string; // 仓位类型：COPY-跟单仓位

  // 本地计算
  floatProfit?: string; // 浮动盈亏
  floatProfitRate?: string; // 收益率
  adl?: number; // adl
  initialMargin: string; // 起始保证金
  liquidationPrice?: string; // 预估强平价
  maintMargin: string; // 维持保证金

  // 其他配置
  contractSize: number; // 面值
  quantityPrecision?: number; // 数量精度
  pricePrecision?: number; // 价格精度
  quoteCoinDisplayPrecision?: number; // 计价币种显示精度
}

export interface AdlItem {
  positionId: string;
  longQuantile: number;
  shortQuantile: number;
  symbol: string;
}

export default class Position {
  @observable positionList: PositionItem[];

  @observable allPositionList: PositionItem[];

  @observable adlList: AdlItem[];

  constructor() {
    this.positionList = [];
    this.allPositionList = [];
    this.adlList = [];
  }

  @action
  setPositionList(data: PositionItem[]) {
    this.positionList = data;
  }

  @action.bound
  async fetchPositionList(params?: { symbol: string }) {
    const result: any = await Axios.get(API.POSITION_LIST, {
      params: {
        symbol: params?.symbol,
      },
    });

    if (Array.isArray(result)) {
      this.setPositionList(result);
    }
  }

  @action
  setAllPositionList(data: PositionItem[]) {
    this.allPositionList = data;
  }

  // 一键平仓 当前持仓
  @action.bound
  async fetchPositionCloseAll(symbol?: string) {
    const result: any = await Axios.post(API.POSITION_COLSE_ALL, { symbol }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || error || i18n.t('failure.position'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      Notification.success({
        message: i18n.t('success.position'),
        offset: 60,
        duration: 2000,
      });
      return Promise.resolve(result);
    }
  }
  // 一键平仓 带单持仓
  @action.bound
  async fetchLeaderOrderCloseAll(symbol?: string) {
    const result: any = await Axios.post(API.LEADER_ORDER_CLOSE_ALL, { symbol }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || error || i18n.t('failure.position'),
        offset: 60,
        duration: 2000,
      });

      if (result) {
        Notification.success({
          message: i18n.t('success.position'),
          offset: 60,
          duration: 2000,
        });
        return Promise.resolve(result);
      }
    })
  }
  @action.bound
  async fetchAllPositionList() {
    const [uBasedPositionList, coinBasedPositionList] = await Promise.all([
      Axios.get(API.POSITION_LIST, {
        baseURL: '/f',
      }),
      Axios.get(API.POSITION_LIST, {
        baseURL: '/d',
      }),
    ]);

    let result: any = [];

    if (Array.isArray(uBasedPositionList)) {
      result = result.concat(uBasedPositionList);
    }

    if (Array.isArray(coinBasedPositionList)) {
      result = result.concat(coinBasedPositionList);
    }

    this.setAllPositionList(result);
  }

  @action.bound
  updatePositionListByRow(params: PositionItem) {
    const { positionId } = params;
    // current underlying position
    const index = this.positionList.findIndex(item => item.positionId === positionId);

    const newList = this.positionList;
    if (index > -1) {
      const target = newList[index];
      const newItem = {
        ...target,
        ...params,
      };
      newList.splice(index, 1, newItem);
    } else {
      newList.unshift(params);
    }

    this.setPositionList(newList);

    // all position
    const indexAll = this.allPositionList.findIndex(item => item.positionId === positionId);

    const newListAll = this.allPositionList;
    if (indexAll > -1) {
      const target = newListAll[indexAll];
      const newItem = {
        ...target,
        ...params,
      };
      newListAll.splice(indexAll, 1, newItem);
    } else {
      newListAll.unshift(params);
    }

    this.setAllPositionList(newListAll);
  }

  // 修改自动追加保证金
  @action.bound
  async fetchUpdateAutoMargin(params: {
    symbol: string;
    positionSide: string;
    autoMargin: boolean;
    positionId: string;
  }) {
    const { positionId, autoMargin } = params;

    try {
      return await Axios.post(API.POSITION_AUTO_MARGIN, params)
        .then(() => {
          const newList = [...this.positionList];
          const index = newList.findIndex((item: PositionItem) => positionId === item.positionId);

          const position = this.positionList[index];
          position.autoMargin = autoMargin;

          newList.splice(index, 1, position);
          this.setPositionList(newList);
        })
        .catch(({ error }: any) => {
          Notification.error({
            message: error?.msg,
            offset: 60,
            duration: 2000,
          });
        });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // 自动减仓等级
  @action
  setAdlList(data: AdlItem[]) {
    this.adlList = data;
  }

  @action.bound
  async fetchAdlList(params?: { symbol: string }) {
    const result: any = await Axios.get(API.POSITION_ADL_LIST, {
      params: {
        symbol: params?.symbol,
      },
    });

    if (Array.isArray(result)) {
      this.setAdlList(result);
    }
  }

  @computed get adlMap() {
    const positionList = this.positionList;
    const adlList = this.adlList;
    const result: { [key: string]: any } = {};

    for (let i = 0; i < positionList.length; i += 1) {
      const item = positionList[i];
      const { positionId } = item;

      const adl = adlList.find(o => o.positionId === positionId);

      if (adl) {
        result[positionId] = adl;
      }
    }

    return result;
  }
}
